import { zodToOpenAPIRegistry } from '@asteasolutions/zod-to-openapi';
import type { BetaMarker } from '@repo/lib';

import { z } from './zod.js';

type Allow = (marker: BetaMarker) => boolean;

const denyAll: Allow = () => false;
const publicVersions = new WeakMap<z.ZodType, z.ZodType>();

export function beta<T extends z.ZodType>(schema: T, marker: BetaMarker = {}): T {
  return schema.meta({ beta: marker }) as T;
}

export function getBetaMarker(schema: z.ZodType): BetaMarker | undefined {
  const marker = (z.globalRegistry.get(schema) as { beta?: unknown } | undefined)?.beta;
  if (marker === true) return {};
  if (marker && typeof marker === 'object') return marker as BetaMarker;
  return undefined;
}

// `beta(z.string()).optional()` puts the marker on the inner schema, so look through the wrappers.
function getFieldMarker(schema: z.ZodType): BetaMarker | undefined {
  const marker = getBetaMarker(schema);
  if (marker) return marker;
  const def = schema._zod.def as z.core.$ZodTypes['_zod']['def'];
  switch (def.type) {
    case 'optional':
    case 'nullable':
    case 'default':
    case 'prefault':
    case 'nonoptional':
    case 'readonly':
    case 'catch':
      return getFieldMarker(def.innerType as z.ZodType);
    case 'pipe':
      return getFieldMarker(def.in as z.ZodType);
    default:
      return undefined;
  }
}

function childSchemas(schema: z.ZodType): z.ZodType[] {
  const def = schema._zod.def as z.core.$ZodTypes['_zod']['def'];
  if (def.type === 'lazy') return [def.getter() as z.ZodType];
  const children: z.ZodType[] = [];
  const values: unknown[] = Object.values(def);
  if (def.type === 'object') values.push(...Object.values((schema as z.ZodObject).shape));
  for (const value of values) {
    for (const item of Array.isArray(value) ? value : [value]) {
      if (item instanceof z.core.$ZodType) children.push(item as z.ZodType);
    }
  }
  return children;
}

function someSchema(
  schema: z.ZodType,
  test: (s: z.ZodType) => boolean,
  seen = new Set<z.ZodType>(),
): boolean {
  if (seen.has(schema)) return false;
  seen.add(schema);
  return test(schema) || childSchemas(schema).some(child => someSchema(child, test, seen));
}

export function getBetaLabs(schema: z.ZodType): string[] {
  const labs = new Set<string>();
  someSchema(schema, s => {
    const lab = getBetaMarker(s)?.lab;
    if (lab) labs.add(lab);
    return false;
  });
  return [...labs].sort();
}

function rebuild(schema: z.ZodType, overrides: Record<string, unknown>): z.ZodType {
  const def = Object.defineProperties({}, Object.getOwnPropertyDescriptors(schema._zod.def));
  for (const [key, value] of Object.entries(overrides)) {
    Object.defineProperty(def, key, {
      value,
      writable: true,
      enumerable: true,
      configurable: true,
    });
  }
  const next = schema.clone(def as typeof schema._zod.def);

  const meta = z.globalRegistry.get(schema);
  if (meta) z.globalRegistry.add(next, meta);
  const openApiMeta = zodToOpenAPIRegistry.get(schema);
  if (openApiMeta) zodToOpenAPIRegistry.add(next, openApiMeta);
  return next;
}

function strip(schema: z.ZodType, allow: Allow): z.ZodType {
  const def = schema._zod.def as z.core.$ZodTypes['_zod']['def'];
  switch (def.type) {
    case 'object': {
      const shape = (schema as z.ZodObject).shape;
      const nextShape: Record<string, z.ZodType> = {};
      let changed = false;
      for (const [key, field] of Object.entries(shape)) {
        const marker = getFieldMarker(field);
        if (marker && !allow(marker)) {
          changed = true;
          continue;
        }
        const nextField = strip(field, allow);
        if (nextField !== field) changed = true;
        nextShape[key] = nextField;
      }
      return changed ? rebuild(schema, { shape: nextShape }) : schema;
    }
    case 'optional':
    case 'nullable':
    case 'default':
    case 'prefault':
    case 'nonoptional':
    case 'readonly':
    case 'catch': {
      const inner = strip(def.innerType as z.ZodType, allow);
      return inner === def.innerType ? schema : rebuild(schema, { innerType: inner });
    }
    case 'array': {
      const element = strip(def.element as z.ZodType, allow);
      return element === def.element ? schema : rebuild(schema, { element });
    }
    case 'union': {
      const options = def.options.map(option => strip(option as z.ZodType, allow));
      return options.every((option, i) => option === def.options[i])
        ? schema
        : rebuild(schema, { options });
    }
    case 'intersection': {
      const left = strip(def.left as z.ZodType, allow);
      const right = strip(def.right as z.ZodType, allow);
      return left === def.left && right === def.right ? schema : rebuild(schema, { left, right });
    }
    case 'pipe': {
      const input = strip(def.in as z.ZodType, allow);
      const output = strip(def.out as z.ZodType, allow);
      return input === def.in && output === def.out
        ? schema
        : rebuild(schema, { in: input, out: output });
    }
    default:
      if (childSchemas(schema).some(child => someSchema(child, s => Boolean(getBetaMarker(s))))) {
        throw new Error(`withoutBeta cannot remove a beta() marker inside a ${def.type} schema`);
      }
      return schema;
  }
}

export function withoutBeta<T extends z.ZodType>(schema: T, allow?: Allow): T {
  if (allow) return strip(schema, allow) as T;
  let stripped = publicVersions.get(schema);
  if (!stripped) {
    stripped = strip(schema, denyAll);
    publicVersions.set(schema, stripped);
  }
  return stripped as T;
}

export function containsBeta(schema: z.ZodType): boolean {
  return Boolean(getFieldMarker(schema)) || withoutBeta(schema) !== schema;
}
